import { useCallback, useMemo, useState } from 'react';
import { SearchInput, Skeleton, StatusMessage } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { TriangleAlert } from 'lucide-react';

import { useBuilderMembers } from '../../hooks/useBuilderMembers';
import { useRoleBuilder } from '../../hooks/useRoleBuilder';
import MemberPickRow from '../shared/MemberPickRow';

/** Step 3 (optional): searchable member list, each with checkbox, avatar, name, email and status. */
function MembersStep() {
  const { t } = useTranslation('people-access');
  const { memberIds, toggleMember } = useRoleBuilder();
  const { members, isLoading, error } = useBuilderMembers();
  const [query, setQuery] = useState('');

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return needle ? members.filter((m) => `${m.name} ${m.email}`.toLowerCase().includes(needle)) : members;
  }, [members, query]);

  const selectedIds = useMemo(() => new Set(memberIds), [memberIds]);

  const handleChange = useCallback((event) => setQuery(event.target.value), []);
  const handleClear = useCallback(() => setQuery(''), []);

  let body;
  if (isLoading) body = <Skeleton height="200px" borderRadius="12px" />;
  else if (error) body = <StatusMessage icon={TriangleAlert} title={t('roles.builder.members.loadError')} />;
  else if (visible.length === 0) body = <p className="text-sm text-content-muted">{t('roles.builder.members.noResults')}</p>;
  else {
    body = (
      <ul className="flex flex-col gap-2" data-testid="role-builder-members">
        {visible.map((member) => (
          <li key={member.id}>
            <MemberPickRow
              member={member}
              selected={selectedIds.has(member.id)}
              onPick={toggleMember}
              ariaLabel={t('roles.builder.members.selectAria', { name: member.name })}
            />
          </li>
        ))}
      </ul>
    );
  }

  return (
    <div className="flex max-w-3xl flex-col gap-4">
      <div>
        <h2 className="text-sm font-semibold text-content">
          {t('roles.builder.members.title')}{' '}
          <span className="font-normal text-content-muted">{t('roles.builder.members.optional')}</span>
        </h2>
        <p className="text-xs text-content-muted">{t('roles.builder.members.hint')}</p>
      </div>
      <SearchInput
        value={query}
        placeholder={t('roles.builder.members.searchPlaceholder')}
        aria-label={t('roles.builder.members.searchAria')}
        onChange={handleChange}
        onClear={handleClear}
      />
      <p className="text-xs text-content-muted" aria-live="polite">
        {t('roles.builder.members.selected', { count: memberIds.length })}
      </p>
      {body}
    </div>
  );
}

export default MembersStep;
